'use client';

import { useCallback, useEffect, useSyncExternalStore } from 'react';
import Link from 'next/link';
import { Cookie } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@kit/ui/button';
import { Trans } from '@kit/ui/trans';

const STORAGE_KEY = 'cookie_consent';

/**
 * Set to `false` to disable the cookie banner entirely.
 * Switzerland does not require a cookie consent banner, so this is disabled
 * by default. Enable if expanding to EU countries that require GDPR banners.
 */
const COOKIE_BANNER_ENABLED = false;

type ConsentValue = 'accepted' | 'rejected';

function getConsentSnapshot(): ConsentValue | null {
  try {
    return localStorage.getItem(STORAGE_KEY) as ConsentValue | null;
  } catch {
    return 'rejected';
  }
}

function subscribeToStorage(callback: () => void) {
  window.addEventListener('storage', callback);
  return () => window.removeEventListener('storage', callback);
}

function getAuthSnapshot(): boolean {
  try {
    return document.cookie
      .split(';')
      .some((c) => c.trim().startsWith('sb-') && c.includes('auth-token'));
  } catch {
    return false;
  }
}

const subscribeNoop = () => () => {};

function updateGtagConsent(granted: boolean) {
  window.gtag?.('consent', 'update', {
    analytics_storage: granted ? 'granted' : 'denied',
  });
}

/**
 * CookieConsentBanner
 *
 * Shows a bottom-of-page banner on first visit asking for cookie consent.
 * Persists the decision to localStorage. Does not block usage.
 *
 * Skips display for authenticated users (they consented at signup).
 * Updates Google Analytics consent mode on accept/reject.
 */
export function CookieConsentBanner() {
  if (!COOKIE_BANNER_ENABLED) return null;

  return <CookieConsentBannerContent />;
}

function CookieConsentBannerContent() {
  const { t } = useTranslation();
  const consent = useSyncExternalStore(
    subscribeToStorage,
    getConsentSnapshot,
    () => null as ConsentValue | null,
  );

  const isAuth = useSyncExternalStore(
    subscribeNoop,
    getAuthSnapshot,
    () => false,
  );

  // Sync GA consent with external system (gtag)
  useEffect(() => {
    if (isAuth || consent === 'accepted') {
      updateGtagConsent(true);
    }
  }, [isAuth, consent]);

  const handleDecision = useCallback((value: ConsentValue) => {
    try {
      localStorage.setItem(STORAGE_KEY, value);
    } catch {
      // ignore
    }

    updateGtagConsent(value === 'accepted');

    // Trigger useSyncExternalStore to re-read
    window.dispatchEvent(new StorageEvent('storage', { key: STORAGE_KEY }));
  }, []);

  // Don't show for authenticated users or if consent already given
  if (isAuth || consent !== null) return null;

  return (
    <div
      role="dialog"
      aria-label={t('common:cookieBanner.ariaLabel')}
      className="bg-background/95 fixed inset-x-0 bottom-0 z-50 border-t p-4 shadow-lg backdrop-blur-sm"
    >
      <div className="mx-auto flex max-w-5xl flex-col gap-3 sm:flex-row sm:items-center sm:gap-6">
        <div className="flex min-w-0 flex-1 items-start gap-3">
          <Cookie
            className="text-muted-foreground mt-0.5 h-5 w-5 shrink-0"
            aria-hidden="true"
          />
          <p className="text-muted-foreground text-sm">
            <Trans
              i18nKey="common:cookieBanner.message"
              components={[
                <Link
                  key="cookie-policy"
                  href="/cookie-policy"
                  className="text-foreground underline underline-offset-4 hover:no-underline"
                />,
              ]}
            />
          </p>
        </div>

        <div className="flex shrink-0 gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleDecision('rejected')}
          >
            <Trans i18nKey="common:cookieBanner.reject" />
          </Button>
          <Button size="sm" onClick={() => handleDecision('accepted')}>
            <Trans i18nKey="common:cookieBanner.accept" />
          </Button>
        </div>
      </div>
    </div>
  );
}
